import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Car, Loader2, Fuel, CalendarClock, XCircle, Check, Star, MapPin, Wallet, ImagePlus, X, ChevronDown, Flag, Phone, MessageCircle, Download, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import ReportDialog from "../../components/app/ReportDialog";
import SearchingPulse from "../../components/app/SearchingPulse";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PageHeader, Section, StatusBadge, EmptyState } from "../../components/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { RatingBreakdown } from "../../components/app/RatingBreakdown";
import PlaceInput from "../../components/app/PlaceInput";
import { useAuth } from "../../context/AuthContext";
import { useLocationCtx } from "../../context/LocationContext";
import { privateCarApi, inr, errorMessage } from "../../lib/api";
import { openCheckout } from "../../lib/razorpay";

const SEATERS = [{ v: 4, label: "4-seater" }, { v: 6, label: "6-seater" }];
const PLATFORM_BASE = 300;
const PLATFORM_GST = 54;
const PLATFORM_CHARGE = 315;

export default function PrivateCars() {
  const { user } = useAuth();
  const { location } = useLocationCtx();
  const [seater, setSeater] = useState(4);
  const [pickup, setPickup] = useState(null);
  const [dest, setDest] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [startAt, setStartAt] = useState("");
  const [days, setDays] = useState(1);
  const [notes, setNotes] = useState("");
  const [cars, setCars] = useState([]);
  const [loadingCars, setLoadingCars] = useState(false);
  const [selected, setSelected] = useState([]);
  const [profileCar, setProfileCar] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [showCharges, setShowCharges] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // Seed the pickup from the user's global location (until they pick a specific spot).
  useEffect(() => {
    if (!pickup && location?.lat != null) setPickup({ lat: location.lat, lng: location.lng, address: location.label });
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const minStart = useMemo(() => {
    const d = new Date(Date.now() + 12 * 3600 * 1000 + 60000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const load = useCallback(() => { privateCarApi.mine().then(setBookings).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const fetchCars = useCallback(() => {
    if (!pickup) { setCars([]); return; }
    setLoadingCars(true);
    privateCarApi.cars({ lat: pickup.lat, lng: pickup.lng, seater })
      .then((c) => { setCars(c); setSelected([]); })
      .catch(() => setCars([]))
      .finally(() => setLoadingCars(false));
  }, [pickup, seater]);
  useEffect(() => { fetchCars(); }, [fetchCars]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const openCharges = () => {
    if (!pickup) { toast.error("Choose a pickup location"); return; }
    if (!dest?.lat) { toast.error("Choose your destination"); return; }
    if (!startAt) { toast.error("Choose a start date & time"); return; }
    if (selected.length === 0) { toast.error("Select at least one private car"); return; }
    setAgreed(false);
    setEstimate(null);
    privateCarApi.tripEstimate({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, dest_lat: dest.lat, dest_lng: dest.lng, days: Number(days) })
      .then(setEstimate).catch(() => setEstimate({ available: false }));
    setShowCharges(true);
  };

  const requestAndPay = async () => {
    if (!pickup) { toast.error("Choose a pickup location"); return; }
    if (!dest?.lat) { toast.error("Choose your destination"); return; }
    if (!startAt) { toast.error("Choose a start date & time"); return; }
    if (selected.length === 0) { toast.error("Select at least one private car"); return; }
    setShowCharges(false);
    setBusy(true);
    try {
      const { booking, razorpay } = await privateCarApi.book({
        car_driver_ids: selected, seater, start_date: new Date(startAt).toISOString(),
        days: Number(days), pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        pickup_address: pickup.name,
        dest_lat: dest?.lat ?? null, dest_lng: dest?.lng ?? null, dest_address: dest?.name ?? null,
        notes: notes.trim() || null,
      });
      let res;
      try {
        res = await openCheckout({ order: razorpay, amount: booking.upfront_amount ?? PLATFORM_CHARGE, user, description: `Private car — ₹315 platform fee` });
      } catch (err) {
        if (err?.cancelled) { toast.error("Payment cancelled — request not sent. Retry anytime."); setBusy(false); return; }
        throw err;
      }
      await privateCarApi.payPlatformFee(booking.booking_id, {
        razorpay_order_id: res.razorpay_order_id || razorpay.order_id,
        razorpay_payment_id: res.razorpay_payment_id,
        razorpay_signature: res.razorpay_signature || null,
      });
      toast.success("Payment received — request sent!", { description: "Selected drivers can now accept. The ₹315 platform fee is refunded only if no driver accepts." });
      setSelected([]); setNotes(""); load();
    } catch (e) { toast.error(errorMessage(e, "Could not send request")); }
    finally { setBusy(false); }
  };

  const cancel = async (id, feeAtRisk) => {
    if (feeAtRisk && !window.confirm("Cancel this booking? The ₹315 platform fee is non-refundable when you cancel.")) return;
    try { await privateCarApi.cancel(id); toast.success("Booking cancelled", { description: "The ₹315 platform fee is non-refundable for customer cancellations." }); load(); }
    catch (e) { toast.error(errorMessage(e, "Could not cancel")); }
  };

  const rateDone = () => load();

  return (
    <>
      <PageHeader title="Private cars" description="Hire a professional driver (with their car) by the day. Pay a flat ₹300 + 5% GST (₹315) platform fee online to send your request (refunded only if no driver accepts, or a driver accepts then cancels; non-refundable if you cancel). You settle the driver's daily fee, petrol (₹15/km round trip) and toll directly with the driver — no extra tax or GST." />
      <div className="mx-auto max-w-2xl space-y-6">
        <Section title="Find a driver to hire" description="Professional drivers (with their own car) for hire near your pickup. Book at least 12 hours ahead.">
          <div className="space-y-4">
            <div>
              <Label>Car size</Label>
              <div className="mt-1.5 flex gap-2" data-testid="seater-toggle">
                {SEATERS.map((s) => (
                  <button key={s.v} type="button" onClick={() => setSeater(s.v)} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${seater === s.v ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft"}`} data-testid={`seater-${s.v}`}>
                    <Car className="mx-auto mb-1 h-4 w-4" /> {s.label}
                  </button>
                ))}
              </div>
            </div>
            <PlaceInput label="Pickup" value={pickup} onChange={setPickup} placeholder="Where should the car arrive?" allowCurrent testId="pc-pickup" />
            <PlaceInput label="Destination" value={dest} onChange={setDest} placeholder="Where are you going?" testId="pc-dest" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date &amp; time</Label><Input type="datetime-local" min={minStart} value={startAt} onChange={(e) => setStartAt(e.target.value)} className="h-11" data-testid="pc-start" /></div>
              <div><Label>Number of days</Label><Input type="number" min="1" max="30" value={days} onChange={(e) => setDays(e.target.value)} className="h-11" data-testid="pc-days" /></div>
            </div>
            <div><Label>Notes for driver (optional)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Trip plan, luggage, stops…" className="h-11" data-testid="pc-notes" /></div>
          </div>
        </Section>

        <Section title="Drivers available near you" description={pickup ? `${cars.length} driver${cars.length === 1 ? "" : "s"} · select one or more` : "Choose a pickup to see drivers"}>
          {!pickup ? (
            <EmptyState icon={MapPin} title="Pick a location first" text="We'll show private cars sorted by distance from your pickup." />
          ) : loadingCars ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : cars.length === 0 ? (
            <EmptyState icon={Car} title="No drivers available" text="No drivers for this car size near your pickup yet. Try the other size." />
          ) : (
            <div className="space-y-2" data-testid="pc-car-list">
              {cars.map((c) => {
                const on = selected.includes(c.driver_id);
                return (
                  <div key={c.driver_id} className={`rounded-2xl border transition-colors ${on ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"}`}>
                  <button type="button" onClick={() => toggle(c.driver_id)} className="w-full p-3 text-left" data-testid={`pc-car-${c.driver_id}`}>
                    {c.photos?.length > 0 && (
                      <div className="mb-2 flex gap-1.5 overflow-x-auto" data-testid={`pc-car-photos-${c.driver_id}`}>
                        {c.photos.slice(0, 5).map((pid) => (
                          <img key={pid} src={privateCarApi.photoUrl(pid)} alt="car" className="h-20 w-28 shrink-0 rounded-lg object-cover" loading="lazy" />
                        ))}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink">Hire {c.driver_name || "this"} · Car Driver{c.top_rated && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-bold text-amber-700" data-testid={`pc-top-rated-${c.driver_id}`}><Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />Top rated</span>}</p>
                        <p className="mt-0.5 text-xs text-ink-soft">{[c.car_make, c.car_model].filter(Boolean).join(" ") || "Private car"}{c.car_year ? ` · ${c.car_year}` : ""} · <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{(c.rating || 4.8).toFixed(1)}</span>{c.rating_count > 0 ? ` (${c.rating_count})` : " · new"}{c.distance_km != null ? ` · ${c.distance_km} km away` : ""}</p>
                        {c.facilities?.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {c.facilities.map((f) => (<span key={f} className="rounded-full bg-surface-100 px-2 py-0.5 text-[10px] font-semibold text-ink-soft">{f}</span>))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-sm font-bold text-ink">{inr(c.daily_rate)}<span className="text-[10px] font-normal text-ink-muted">/day</span></span>
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${on ? "border-brand bg-brand text-white" : "border-surface-300 text-transparent"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center justify-between gap-2 border-t border-surface-200 px-3 py-2">
                    <button type="button" onClick={() => setProfileCar(c)} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand" data-testid={`pc-view-profile-${c.driver_id}`}>
                      <Eye className="h-3.5 w-3.5" /> View profile &amp; photos
                    </button>
                    <button type="button" onClick={() => toggle(c.driver_id)} className={`rounded-full px-3 py-1 text-xs font-bold ${on ? "bg-brand text-white" : "bg-surface-100 text-ink-soft ring-1 ring-surface-300"}`} data-testid={`pc-select-${c.driver_id}`}>{on ? "Selected" : "Select"}</button>
                  </div>
                  {c.rating_count > 0 && <CarReviews driverId={c.driver_id} />}
                  </div>
                );
              })}
              <div className="flex items-start gap-2 rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand" data-testid="pc-fee-note">
                <Fuel className="mt-0.5 h-3.5 w-3.5 shrink-0" /> You are hiring the driver (with their car). A flat ₹300 + 5% GST (₹315) platform fee is paid now to send your request (refunded if no driver accepts). The driver's daily fee, the driver's car petrol and toll are paid directly to the driver.
              </div>
              <Button onClick={openCharges} disabled={busy || selected.length === 0} className="h-12 w-full rounded-full bg-brand text-base text-white hover:bg-brand-600" data-testid="pc-book">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Review charges &amp; book {selected.length || ""} car{selected.length === 1 ? "" : "s"}
              </Button>
            </div>
          )}
        </Section>

        <Dialog open={showCharges} onOpenChange={(o) => { if (!o) setShowCharges(false); }}>
          <DialogContent className="max-w-md" data-testid="pc-charges-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-brand" /> Charges for this booking</DialogTitle>
              <DialogDescription>Please review and accept these charges before we send your request.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="space-y-2" data-testid="pc-charges-cars">
                {cars.filter((c) => selected.includes(c.driver_id)).map((c) => {
                  const fare = Number(c.daily_rate || 0) * Number(days || 1);
                  return (
                    <div key={c.driver_id} className="rounded-xl border border-surface-300 p-2.5">
                      <p className="font-semibold text-ink">{c.driver_name || "Private car"}</p>
                      <p className="text-ink-soft">Driver charge: {inr(c.daily_rate)}/day × {days} day{days > 1 ? "s" : ""} = <strong className="text-ink">{inr(fare)}</strong></p>
                    </div>
                  );
                })}
              </div>
              <div className="rounded-xl bg-amber-50 p-3 text-amber-800" data-testid="pc-charges-extras">
                <p className="flex items-center gap-1.5 font-semibold"><Fuel className="h-4 w-4" /> Petrol &amp; toll tax — paid separately</p>
                {estimate?.available ? (
                  <div className="mt-1.5 space-y-0.5 text-xs" data-testid="pc-charges-estimate">
                    <p>Est. petrol: <strong>{inr(estimate.petrol_estimate)}</strong> <span className="text-amber-700/80">(~{estimate.total_km} km round-trip × {days} day{days > 1 ? "s" : ""} @ ₹{estimate.petrol_per_km}/km)</span></p>
                    {estimate.toll_estimate != null ? (
                      <p>Est. toll: <strong>{inr(estimate.toll_estimate)}</strong> <span className="text-amber-700/80">(round-trip × {days} day{days > 1 ? "s" : ""})</span></p>
                    ) : estimate.toll_present ? (
                      <p>Toll applies on this route — <strong>estimate unavailable</strong>, paid to driver</p>
                    ) : (
                      <p>No tolls expected on this route</p>
                    )}
                    <p className="pt-0.5 italic text-amber-700/80">Approximate — actual petrol &amp; toll are paid directly to the driver.</p>
                  </div>
                ) : (
                  <p className="mt-1 text-xs">Actual petrol and toll costs are paid by you <strong>directly to the driver</strong> during the trip. Add a destination above to see an approximate estimate. These are not collected in the app.</p>
                )}
              </div>
              <div className="rounded-xl bg-brand-50 p-3 text-brand" data-testid="pc-charges-platform">
                <div className="flex items-center justify-between"><span className="text-sm font-semibold">Payable now (online)</span><span className="text-sm font-bold">₹315</span></div>
                <p className="mt-0.5 text-xs">₹{PLATFORM_BASE} + 5% GST = <strong>₹{PLATFORM_CHARGE}</strong> platform fee only. It is refunded <strong>only if no driver accepts</strong> (or a driver accepts then cancels); <strong>if you cancel it is non-refundable</strong>. You settle the driver's daily fee, petrol (₹15/km round trip) and toll <strong>directly with the driver</strong> — outside the app, with no extra tax or GST.</p>
              </div>
              <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-surface-300 p-2.5" data-testid="pc-charges-agree-label">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand" data-testid="pc-charges-agree" />
                <span className="text-xs text-ink-soft">I agree to pay the ₹315 platform fee now (non-refundable if I cancel), and to settle the driver's daily fee, petrol (₹15/km round trip) &amp; toll directly with the driver.</span>
              </label>
            </div>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" onClick={() => setShowCharges(false)} className="flex-1 rounded-full" data-testid="pc-charges-deny">Cancel</Button>
              <Button onClick={requestAndPay} disabled={!agreed || busy} className="flex-1 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="pc-charges-accept">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Agree &amp; pay
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <CarProfileDialog
          car={profileCar}
          onClose={() => setProfileCar(null)}
          selected={profileCar ? selected.includes(profileCar.driver_id) : false}
          onToggle={() => { if (profileCar) { toggle(profileCar.driver_id); } }}
        />

        {bookings.length > 0 && (
          <Section title="Your private car bookings">
            <div className="space-y-2" data-testid="pc-bookings">
              {bookings.map((b) => (
                <div key={b.booking_id} className="rounded-2xl border border-surface-300 bg-white p-3" data-testid={`pc-booking-${b.booking_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><CalendarClock className="h-4 w-4 text-brand" /> {new Date(b.start_date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {b.days} day{b.days > 1 ? "s" : ""}</span>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="mt-1 text-xs text-ink-soft">{b.seater}-seater · {b.pickup_address}</p>
                  {b.status === "requested" && (
                    <div className="mt-2 rounded-2xl bg-brand-50/60 py-2" data-testid={`pc-requested-${b.booking_id}`}>
                      <SearchingPulse
                        title="Waiting for a driver to accept…"
                        subtitle={`Request sent to ${(b.candidate_driver_ids || []).length} driver${(b.candidate_driver_ids || []).length > 1 ? "s" : ""} — the first to accept is yours.`}
                        icon={<Car className="h-6 w-6" />}
                        testId={`pc-requested-pulse-${b.booking_id}`}
                      />
                      <p className="text-center text-xs text-ink-muted">₹315 platform fee paid · refunded if no driver accepts</p>
                    </div>
                  )}
                  {b.status === "no_driver" && <p className="mt-1 text-xs text-red-600">No driver accepted — your ₹315 platform fee was refunded.</p>}
                  {(b.status === "confirmed" || b.status === "completed" || b.status === "settled") && (
                    <div className="mt-2 rounded-xl bg-surface-50 p-2 text-xs text-ink-soft" data-testid={`pc-fare-${b.booking_id}`}>
                      <p><span className="font-semibold text-ink">{[b.car_make, b.car_model].filter(Boolean).join(" ") || "Private car"}</span> · {b.driver_name}{b.driver_phone ? ` · ${b.driver_phone}` : ""}</p>
                      <p className="mt-0.5">{inr(b.daily_rate)}/day × {b.days} = <span className="font-semibold text-ink">{inr(b.fare)}</span> <span className="text-ink-muted">(estimate — settle directly with the driver, plus petrol &amp; toll)</span></p>
                      <p className="mt-0.5 text-ink-muted">Platform fee ₹315 paid · all fare, petrol &amp; toll settled directly with the driver.</p>
                    </div>
                  )}
                  {b.status === "confirmed" && (
                    <div className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`pc-confirm-note-${b.booking_id}`}>
                      <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>Please contact your driver now to confirm the route, toll, petrol and fare before the trip. Our team will also reach out to help confirm the details.</span>
                    </div>
                  )}
                  {(b.status === "confirmed" || b.status === "completed" || b.status === "settled") && b.driver_id && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`pc-contact-${b.booking_id}`}>
                      {b.driver_phone && <a href={`tel:${b.driver_phone}`} className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white" data-testid={`pc-call-${b.booking_id}`}><Phone className="h-3.5 w-3.5" /> Call driver</a>}
                      <Link to={`/app/chat/${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-3 py-1.5 text-xs font-semibold text-ink-soft ring-1 ring-surface-300" data-testid={`pc-chat-${b.booking_id}`}><MessageCircle className="h-3.5 w-3.5" /> Chat</Link>
                      {(b.status === "completed" || b.status === "settled") && <a href={privateCarApi.invoicePdfUrl(b.booking_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-3 py-1.5 text-xs font-semibold text-ink-soft ring-1 ring-surface-300" data-testid={`pc-receipt-${b.booking_id}`}><Download className="h-3.5 w-3.5" /> Receipt</a>}
                      <ReportDialog contextType="private_car" contextId={b.booking_id} triggerLabel="Report" triggerClassName="h-auto px-3 py-1.5 text-xs" />
                    </div>
                  )}
                  {b.status === "settled" && <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" /> Settled</p>}
                  {(b.status === "completed" || b.status === "settled") && !b.rated && (
                    <TripRating bookingId={b.booking_id} onDone={rateDone} />
                  )}
                  {b.rated && <p className="mt-1 inline-flex items-center gap-0.5 text-xs text-ink-muted">You rated {b.rating_stars}<Star className="h-3 w-3 fill-amber-400 text-amber-400" /></p>}
                  {(b.status === "pending_payment" || b.status === "requested" || b.status === "confirmed") && (
                    <button type="button" onClick={() => cancel(b.booking_id, b.status === "requested" || b.status === "confirmed")} className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`pc-cancel-${b.booking_id}`}><XCircle className="h-3.5 w-3.5" /> Cancel</button>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </>
  );
}

function CarProfileDialog({ car, onClose, selected, onToggle }) {
  const [data, setData] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  useEffect(() => {
    setData(null); setActivePhoto(0);
    if (!car || !(car.rating_count > 0)) return;
    let alive = true;
    privateCarApi.reviews(car.driver_id)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ reviews: [], breakdown: null, rating_count: 0 }); });
    return () => { alive = false; };
  }, [car]);
  if (!car) return null;
  const photos = car.photos || [];
  return (
    <Dialog open={!!car} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pc-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-brand" /> {car.driver_name || "Private car driver"}</DialogTitle>
          <DialogDescription>{[car.car_make, car.car_model].filter(Boolean).join(" ") || "Private car"}{car.car_year ? ` · ${car.car_year}` : ""}{car.seater ? ` · ${car.seater}-seater` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {photos.length > 0 ? (
            <div data-testid="pc-profile-gallery">
              <div className="overflow-hidden rounded-2xl bg-surface-100">
                <img src={privateCarApi.photoUrl(photos[activePhoto])} alt="car" className="h-56 w-full object-cover" />
              </div>
              {photos.length > 1 && (
                <div className="mt-2 flex gap-1.5 overflow-x-auto">
                  {photos.map((pid, i) => (
                    <button key={pid} type="button" onClick={() => setActivePhoto(i)} className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg ring-2 ${i === activePhoto ? "ring-brand" : "ring-transparent"}`} data-testid={`pc-profile-thumb-${i}`}>
                      <img src={privateCarApi.photoUrl(pid)} alt="car thumb" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center rounded-2xl bg-surface-100 text-ink-muted"><Car className="h-10 w-10" /></div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft">
            <span className="inline-flex items-center gap-1 font-semibold text-ink"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{(car.rating || 4.8).toFixed(1)}{car.rating_count > 0 ? ` (${car.rating_count} ratings)` : " · new driver"}</span>
            {car.top_rated && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700"><Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />Top rated</span>}
            {car.distance_km != null && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{car.distance_km} km away</span>}
          </div>
          {car.base_address && <p className="text-xs text-ink-muted">Based near {car.base_address}</p>}
          {car.facilities?.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold text-ink">Features &amp; facilities</p>
              <div className="flex flex-wrap gap-1.5" data-testid="pc-profile-facilities">
                {car.facilities.map((f) => (<span key={f} className="rounded-full bg-surface-100 px-2.5 py-1 text-[11px] font-semibold text-ink-soft">{f}</span>))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between rounded-xl bg-brand-50 px-3 py-2.5">
            <span className="text-xs font-semibold text-brand">Daily driver charge</span>
            <span className="text-base font-bold text-ink">{inr(car.daily_rate)}<span className="text-[10px] font-normal text-ink-muted">/day</span></span>
          </div>
          {car.rating_count > 0 && (
            <div data-testid="pc-profile-reviews">
              <p className="mb-1.5 text-xs font-semibold text-ink">Ratings &amp; reviews</p>
              {data === null ? (
                <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-brand" /></div>
              ) : (
                <div className="space-y-2">
                  {data.breakdown && data.rating_count > 0 && <RatingBreakdown breakdown={data.breakdown} total={data.rating_count} testId="pc-profile-breakdown" />}
                  {(data.reviews || []).slice(0, 5).map((rv) => (
                    <div key={rv.review_id} className="rounded-lg bg-surface-50 p-2">
                      <p className="text-[11px] font-semibold text-ink">{rv.customer_name || "Customer"} · {rv.stars}★</p>
                      {rv.comment && <p className="text-[11px] text-ink-soft">{rv.comment}</p>}
                      {rv.driver_reply && <p className="mt-1 rounded bg-white px-2 py-1 text-[10px] text-ink-muted"><span className="font-semibold">Driver:</span> {rv.driver_reply}</p>}
                    </div>
                  ))}
                  {(data.reviews || []).length === 0 && <p className="text-[11px] text-ink-muted">No written reviews yet.</p>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-2 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full" data-testid="pc-profile-close">Close</Button>
          <Button onClick={() => { onToggle(); onClose(); }} className={`flex-1 rounded-full ${selected ? "bg-ink text-white hover:bg-ink/90" : "bg-brand text-white hover:bg-brand-600"}`} data-testid="pc-profile-select">{selected ? "Remove from booking" : "Select this car"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CarReviews({ driverId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [reported, setReported] = useState({});
  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data) {
      try { setData(await privateCarApi.reviews(driverId)); }
      catch { setData({ reviews: [], breakdown: null, rating_count: 0 }); }
    }
  };
  const report = async (reviewId) => {
    const reason = window.prompt("Report this review — why is it inappropriate? (optional)") ?? "";
    try {
      await privateCarApi.reportReview(reviewId, reason.trim());
      setReported((r) => ({ ...r, [reviewId]: true }));
      toast.success("Reported to moderators. Thank you.");
    } catch (e) { toast.error(errorMessage(e, "Could not report this review")); }
  };
  return (
    <div className="border-t border-surface-200 px-3 py-2">
      <button type="button" onClick={toggleOpen} className="flex w-full items-center justify-between text-xs font-semibold text-brand" data-testid={`pc-reviews-toggle-${driverId}`}>
        <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> Ratings &amp; reviews</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`pc-reviews-${driverId}`}>
          {data === null ? (
            <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-brand" /></div>
          ) : (
            <>
              {data.breakdown && data.rating_count > 0 && <RatingBreakdown breakdown={data.breakdown} total={data.rating_count} testId={`pc-reviews-breakdown-${driverId}`} />}
              {(data.reviews || []).slice(0, 5).map((rv) => (
                <div key={rv.review_id} className="rounded-lg bg-surface-50 p-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[11px] font-semibold text-ink">{rv.customer_name || "Customer"} · {rv.stars}★</p>
                    {reported[rv.review_id] ? (
                      <span className="shrink-0 text-[10px] font-semibold text-ink-muted">Reported</span>
                    ) : (
                      <button type="button" onClick={() => report(rv.review_id)} className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-semibold text-ink-muted hover:text-red-600" data-testid={`pc-review-report-${rv.review_id}`}><Flag className="h-2.5 w-2.5" />Report</button>
                    )}
                  </div>
                  {rv.comment && <p className="text-[11px] text-ink-soft">{rv.comment}</p>}
                  {rv.photo_ids?.length > 0 && (
                    <div className="mt-1 flex gap-1 overflow-x-auto">
                      {rv.photo_ids.map((pid) => (<img key={pid} src={privateCarApi.photoUrl(pid)} alt="review" className="h-14 w-14 shrink-0 rounded object-cover" loading="lazy" />))}
                    </div>
                  )}
                  {rv.driver_reply && <p className="mt-1 rounded bg-white px-2 py-1 text-[10px] text-ink-muted"><span className="font-semibold">Driver:</span> {rv.driver_reply}</p>}
                </div>
              ))}
              {(data.reviews || []).length === 0 && <p className="text-[11px] text-ink-muted">No written reviews yet.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function TripRating({ bookingId, onDone }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 3) { toast.error("Up to 3 photos."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB."); return; }
    const file_data = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
    setPhotos((p) => [...p, { file_data, mime: file.type, preview: URL.createObjectURL(file) }]);
  };

  const submit = async () => {
    if (!stars) { toast.error("Tap a star to rate first."); return; }
    setBusy(true);
    try {
      await privateCarApi.rate(bookingId, { stars, comment: comment.trim() || null, photos: photos.map((p) => ({ file_data: p.file_data, mime: p.mime })) });
      toast.success("Thanks for rating!");
      onDone();
    } catch (e) { toast.error(errorMessage(e, "Could not submit rating")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-xl border border-surface-300 bg-surface-50 p-2.5" data-testid={`pc-rate-${bookingId}`}>
      <div className="flex items-center gap-1">
        <span className="mr-1 text-xs font-medium text-ink-soft">Rate your trip:</span>
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} type="button" onClick={() => setStars(s)} className={s <= stars ? "text-amber-400" : "text-ink-muted hover:text-amber-400"} data-testid={`pc-star-${bookingId}-${s}`} aria-label={`${s} stars`}>
            <Star className={`h-6 w-6 ${s <= stars ? "fill-amber-400" : ""}`} />
          </button>
        ))}
      </div>
      <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment (optional)" className="mt-2 h-9" data-testid={`pc-rate-comment-${bookingId}`} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {photos.map((p, i) => (
          <div key={i} className="relative h-14 w-14 overflow-hidden rounded-lg border border-surface-300">
            <img src={p.preview} alt="review" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {photos.length < 3 && (
          <label className="flex h-14 w-14 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-surface-300 text-ink-soft hover:border-brand hover:text-brand" data-testid={`pc-rate-photo-add-${bookingId}`}>
            <ImagePlus className="h-4 w-4" />
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={addPhoto} />
          </label>
        )}
        <Button onClick={submit} disabled={busy} size="sm" className="ml-auto rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`pc-rate-submit-${bookingId}`}>
          {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />} Submit
        </Button>
      </div>
    </div>
  );
}
